import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorToastMessage } from '../../../../components/layout/toast-root/toast';
import { useFileList } from '../../../../hooks/use-file-list';
import { useGetFilePaths } from '../../../../redux/api/appConfigApi';
import type { IPostsViewComment } from '../../../../redux/api/posts/posts-api.types';
import {
  useAddPostCommentReaction,
  useDeletePostComment,
  usePostPutPostComment,
} from '../../../../redux/api/postsApi';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { FileVisibility } from '../../../../shared/constants';
import { IAppFile } from '../../../../types/interfaces/app-file';
import { IComment } from '../../../../types/interfaces/comment';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../../utils/files/firebase-storage-utils';

export function useComment(
  comment: IPostsViewComment,
  refreshPosts?: () => void,
) {
  const { t } = useTranslation();
  const [addCommentReaction] = useAddPostCommentReaction();
  const [postPutPostComment] = usePostPutPostComment();
  const [deletePostComment] = useDeletePostComment();
  const { data: user } = useGetMyDetails();
  const { data: filePaths } = useGetFilePaths();

  const {
    fileIdsToDelete,
    filesToUpload,
    resultFiles: commentFiles,
    onDeleteFile,
    onUploadFiles,
    resetFilesState,
  } = useFileList(comment.files ?? []);
  const [editedComment, setEditedComment] = useState<IPostsViewComment | null>(
    null,
  );
  const [userReaction, setUserReaction] = useState<boolean>(null);
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [isDeletingComment, setIsDeletingComment] = useState(false);

  useEffect(() => {
    if (userReaction === null) {
      setUserReaction(
        !!comment.reactions?.find((reaction) => reaction.userId === user.id),
      );
    }
  }, [comment]);

  function onToggleToEditComment() {
    setEditedComment(comment);
  }

  function onCommentEdit(newValue: string) {
    setEditedComment({ ...comment, content: newValue });
  }

  async function onCommentSave() {
    if (!user || !filePaths || !user.uid || !user.id) {
      return;
    }

    setIsSavingComment(true);
    const uploadedStoragePaths: string[] = [];
    let hasError = false;

    try {
      const uploadedFiles: IAppFile[] = await Promise.all(
        filesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.posts,
            user.uid,
            user.id,
            FileVisibility.public,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);

          return uploadedFile;
        }),
      );

      await postPutPostComment({
        postId: comment.postId,
        id: comment.id,
        body: {
          comment: { ...comment, ...editedComment } as IComment,
          fileIdsToDelete,
          filesToUpload: uploadedFiles,
        },
      }).unwrap();

      refreshPosts && refreshPosts();
      setEditedComment(null);
      resetFilesState();
    } catch (error) {
      hasError = true;
      console.error(
        'Error uploading files to Firebase Storage or saving comment:',
        error,
      );
      errorToastMessage(t('messages.uploadOrSaveFailed'));
    } finally {
      if (hasError && uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
      setIsSavingComment(false);
    }
  }

  function onCommentReaction() {
    if (!comment.postId) return;

    return addCommentReaction({ postId: comment.postId, id: comment.id })
      .then(() => setUserReaction((prev) => !prev))
      .catch((e) => console.log(e));
  }

  function onCommentRemove() {
    if (!comment.postId) return;

    setIsDeletingComment(true);

    return deletePostComment({ postId: comment.postId, id: comment.id })
      .then((response) => {
        if ('error' in response) console.log(response);
        else refreshPosts && refreshPosts();
      })
      .finally(() => {
        setIsDeletingComment(false);
      });
  }

  function onCommentCancel() {
    resetFilesState();
    setEditedComment(null);
  }

  return {
    onCommentRemove,
    onCommentSave,
    onCommentReaction,
    onCommentEdit,
    onToggleToEditComment,
    editedComment,
    commentFiles,
    onDeleteFile,
    onUploadFiles,
    onCommentCancel,
    userReaction,
    user,
    isSavingComment,
    isDeletingComment,
  };
}
